/**
 * ADMIN MODULE — Email Nurture
 * One screen, three sub-tabs:
 *   Subscribers — monitor the deterministic engine (funnel, list, manual controls)
 *   Campaigns   — campaigns → steps (copy, timing, per-step template; DB-backed)
 *   Templates   — reusable branded email formats (logo, colors) with live preview
 */

import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout.jsx";
import NurtureCampaignsPanel from "../components/NurtureCampaignsPanel.jsx";
import NurtureTemplateBuilderPanel from "../components/NurtureTemplateBuilderPanel.jsx";
import {
  listSubscribers,
  getFunnelMetrics,
  pauseSubscriber,
  resumeSubscriber,
  markConverted,
  resendStep,
} from "../utils/nurtureService.js";

const STATUSES = ["all", "active", "paused", "completed", "converted", "unsubscribed", "bounced"];
const STATUS_COLOR = {
  active: "var(--success)", paused: "var(--warn)", completed: "var(--muted)",
  converted: "var(--o-text)", unsubscribed: "var(--muted)", bounced: "var(--error)",
};

function Stat({ label, value, sub }) {
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "16px 18px", background: "var(--surface)", minWidth: 130 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: "var(--heading)" }}>{value}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--muted)", marginTop: 2 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Pill({ status }) {
  const c = STATUS_COLOR[status] || "var(--muted)";
  // color-mix, not `${c}1f` — the colours are now tokens, and you cannot
  // staple an alpha suffix onto a var() reference.
  return <span style={{ fontSize: 11, fontWeight: 700, color: c, background: `color-mix(in srgb, ${c} 14%, transparent)`, padding: "2px 9px", borderRadius: 999 }}>{status}</span>;
}

function fmt(ts) { try { return ts ? new Date(ts).toLocaleString() : "—"; } catch { return ts; } }

function SubscribersPanel() {
  const [metrics, setMetrics] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [m, r] = await Promise.all([getFunnelMetrics(), listSubscribers({ status })]);
      setMetrics(m); setRows(r);
    } catch (e) { setErr(e.message || "Could not load."); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  async function act(id, fn) {
    setBusyId(id); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr(e.message || "Action failed."); } finally { setBusyId(null); }
  }

  const m = metrics;
  return (
    <>
      {err && (
        <div className="admin-banner" role="alert" style={{ marginBottom: 16 }}>
          <span>{err} {/^Could not load/.test(err) ? "— if the nurture tables aren't set up yet, apply migrations 0047 & 0048." : ""}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <Stat label="Enrolled" value={m ? m.enrolled : "…"} />
        <Stat label="Active" value={m ? m.active : "…"} sub={m ? `${m.paused} paused` : null} />
        <Stat label="Sends" value={m ? m.sends : "…"} />
        <Stat label="Open rate" value={m ? `${m.openRate}%` : "…"} sub={m ? `${m.opens} opens` : null} />
        <Stat label="Click rate" value={m ? `${m.clickRate}%` : "…"} sub={m ? `${m.clicks} clicks` : null} />
        <Stat label="Converted" value={m ? m.converted : "…"} />
        <Stat label="Completed" value={m ? m.completed : "…"} />
        <Stat label="Unsub / Bounced" value={m ? `${m.unsubscribed} / ${m.bounced}` : "…"} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)" }}>
          Status{" "}
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ marginLeft: 6 }}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button className="btn" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>{rows.length} shown</span>
      </div>

      <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--surface)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: "var(--sunken)", textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "10px 14px" }}>Email</th>
                <th style={{ padding: "10px 14px" }}>Status</th>
                <th style={{ padding: "10px 14px" }}>Step</th>
                <th style={{ padding: "10px 14px" }}>Next send</th>
                <th style={{ padding: "10px 14px" }}>Source</th>
                <th style={{ padding: "10px 14px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const busy = busyId === r.id;
                return (
                  <tr key={r.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                    <td style={{ padding: "10px 14px" }}>{r.email}</td>
                    <td style={{ padding: "10px 14px" }}><Pill status={r.status} /></td>
                    <td style={{ padding: "10px 14px" }}>{r.current_step}</td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)" }}>{fmt(r.next_send_at)}</td>
                    <td style={{ padding: "10px 14px", color: "var(--muted)" }}>{r.source || "—"}</td>
                    <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {r.status === "active" ? (
                          <button className="btn" disabled={busy} onClick={() => act(r.id, () => pauseSubscriber(r.id))}>Pause</button>
                        ) : (r.status === "paused" || r.status === "completed") ? (
                          <button className="btn" disabled={busy} onClick={() => act(r.id, () => resumeSubscriber(r.id))}>Resume</button>
                        ) : null}
                        {r.status !== "converted" && (
                          <button className="btn" disabled={busy} onClick={() => act(r.id, () => markConverted(r.id))}>Converted</button>
                        )}
                        <button className="btn" disabled={busy} title="Re-send from this subscriber's current step"
                          onClick={() => act(r.id, () => resendStep(r.id, r.current_step))}>Resend</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} style={{ padding: "24px 14px", textAlign: "center", color: "var(--muted)" }}>No subscribers.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const TABS = [
  { key: "subscribers", label: "Subscribers" },
  { key: "campaigns", label: "Campaigns" },
  { key: "templates", label: "Templates" },
];

export default function EmailNurtureView() {
  const [tab, setTab] = useState("subscribers");
  return (
    <AdminLayout title="Email Nurture" subtitle="Subscriber sequences, send automation, and email copy — staff only.">
      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 22 }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                border: "none", background: "none", cursor: "pointer",
                padding: "10px 16px", fontSize: 14, fontWeight: 700,
                color: active ? "var(--o-text)" : "var(--muted)",
                borderBottom: `2px solid ${active ? "var(--o-text)" : "transparent"}`,
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "subscribers" ? <SubscribersPanel />
        : tab === "campaigns" ? <NurtureCampaignsPanel />
        : <NurtureTemplateBuilderPanel />}
    </AdminLayout>
  );
}
